import { sanitizeFlowDefinitionV2Edges } from '../studioV2FlowModel';
import { analyzeStudioV2FlowGraph } from '../../../utils/studioV2FlowDiagnostics.js';
import { filterStaleImportWarnings } from '../../../utils/legacyFlowToV2Client';

export function parseFlowText(text) {
  try {
    const d = JSON.parse(text || '');
    return d && typeof d === 'object' && !Array.isArray(d) ? d : null;
  } catch {
    return null;
  }
}

export const flowNameOf = (def) => (def?.name && String(def.name).trim()) || 'Untitled flow';

/** Plain-language name + one-line description for each step type (falls back to catalog metadata). */
export const STEP_COPY = {
  start: ['Start', 'Where the flow begins and receives its input'],
  end: ['Finish', 'Ends the flow and returns the result'],
  step: ['Step', 'A general processing step'],
  llm: ['AI step', 'Ask an AI model to write, decide or extract'],
  api: ['Call a web service', 'Send a request to another system'],
  code: ['Run code', 'Custom logic written in Python'],
  conditional: ['Branch', 'Go one way or another based on a condition'],
  'human-input': ['Wait for a person', 'Pause until someone approves or replies'],
  parallel: ['Do in parallel', 'Run several branches at the same time'],
  join: ['Wait for branches', 'Continue once parallel branches finish'],
  aggregate: ['Summarize data', 'Count, sum or average a list'],
  'ai-agent': ['AI agent', 'An AI that can reason and use tools'],
  'api-call': ['API call', 'Call an external API'],
  calendar: ['Calendar', 'Create or look up calendar events'],
  chat: ['Chat reply', 'Send a message in a conversation'],
  'code-executor': ['Code snippet', 'Run a short code snippet'],
  connector: ['Connector', 'Use a saved integration'],
  'data-validation': ['Check data', 'Make sure data has the right shape'],
  database: ['Database', 'Read or write your database'],
  db_delete: ['Delete records', 'Remove records from a table'],
  db_insert: ['Add records', 'Insert new records into a table'],
  db_query: ['Find records', 'Look up records in a table'],
  db_update: ['Update records', 'Change existing records'],
  debugger: ['Checkpoint', 'Pause and inspect values while testing'],
  'decision-tree': ['Decision tree', 'Pick a path from several rules'],
  delay: ['Wait', 'Pause for a set amount of time'],
  email: ['Send email', 'Email someone from the flow'],
  'execute-flow': ['Run another flow', 'Reuse another flow as a step'],
  'file-operations': ['Files', 'Read or write files'],
  filter: ['Filter a list', 'Keep only the items that match'],
  form: ['Form', 'Collect structured answers'],
  group: ['Group a list', 'Group items by a field'],
  http_request: ['Web request', 'Low-level HTTP request'],
  logger: ['Log a message', 'Write a note to the run log'],
  loop: ['Repeat for each', 'Run steps for every item in a list'],
  'mcp-tool': ['Use a tool', 'Call a connected tool'],
  memory: ['Remember', 'Store or recall conversation memory'],
  'memory-store': ['Memory store', 'Long-term key and value memory'],
  merge: ['Merge data', 'Combine several values into one'],
  'message-queue': ['Message queue', 'Publish or read queue messages'],
  policy: ['Policy check', 'Check content against your rules'],
  rag: ['Search documents', 'Answer using your uploaded documents'],
  sms: ['Send a text', 'Send an SMS message'],
  'social-post': ['Social post', 'Publish to a social account'],
  sort: ['Sort a list', 'Order items by a field'],
  switch: ['Switch', 'Choose one of many paths by value'],
  tool: ['Built-in tool', 'Run a built-in tool'],
  transform: ['Reshape data', 'Map fields from one shape to another'],
  trigger: ['Trigger', 'Start from an outside event'],
  'try-catch': ['Handle errors', 'Catch failures and recover'],
  'voice-call': ['Voice call', 'Place an automated phone call'],
  'web-crawler': ['Read a web page', 'Fetch and extract content from a site'],
  webhook: ['Webhook', 'Send data to a webhook address'],
};

export function stepCopy(type, meta = null) {
  const hit = STEP_COPY[type];
  if (hit) return { name: hit[0], description: hit[1] };
  const sub = meta?.subtitle && !/^Executor ·/.test(meta.subtitle) ? meta.subtitle : '';
  return { name: meta?.title || type, description: sub };
}

export const COMMON_STEP_TYPES = ['llm', 'conditional', 'api', 'human-input', 'transform', 'end'];

export const PALETTE_GROUP_LABELS = {
  Control: 'Start & finish',
  Core: 'Basics',
  AI: 'AI',
  Logic: 'Logic & branching',
  Data: 'Data',
  Integration: 'Connect to services',
  Communication: 'Messages',
  Utility: 'Utilities',
};

const GROUP_ORDER = ['Control', 'Core', 'AI', 'Logic', 'Data', 'Integration', 'Communication', 'Utility'];

/** Groups catalog entries for the palette; `query` filters by friendly name, description and type. */
export function paletteGroups(catalog, query = '') {
  const q = String(query || '').trim().toLowerCase();
  const groups = new Map();
  Object.entries(catalog || {}).forEach(([type, meta]) => {
    const copy = stepCopy(type, meta);
    if (q && !`${type} ${copy.name} ${copy.description} ${meta.title || ''}`.toLowerCase().includes(q)) return;
    const key = meta.paletteGroup || 'Other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ type, meta, ...copy });
  });
  const rank = (g) => {
    const i = GROUP_ORDER.indexOf(g);
    return i === -1 ? GROUP_ORDER.length : i;
  };
  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([key, items]) => ({ key, label: PALETTE_GROUP_LABELS[key] || key, items }));
}

/** `aiProblems`: output of `aiStepProblems` once the model status is known; omit to skip the AI check. */
export function flowReadiness(def, { aiProblems } = {}) {
  if (!def) {
    return {
      ready: false,
      done: 0,
      total: 1,
      items: [{
        id: 'valid',
        label: 'The flow can be read',
        ok: false,
        fix: 'json',
        fixLabel: 'Open JSON',
        detail: 'The flow definition has a formatting problem.',
      }],
    };
  }
  let check = { errors: [], warnings: [] };
  try {
    check = analyzeStudioV2FlowGraph(sanitizeFlowDefinitionV2Edges(def));
  } catch {
    /* treat as no findings */
  }
  const nodes = Array.isArray(def.nodes) ? def.nodes : [];
  const imports = Array.isArray(def.metadata?.warnings)
    ? filterStaleImportWarnings(def.metadata.warnings.filter(Boolean).map(String))
    : [];
  const items = [
    {
      id: 'start', label: 'Has a Start step', ok: nodes.some((n) => n?.type === 'start'), fix: 'build', fixLabel: 'Open canvas',
    },
    {
      id: 'end', label: 'Has a Finish step', ok: nodes.some((n) => n?.type === 'end'), fix: 'build', fixLabel: 'Open canvas',
    },
    {
      id: 'errors',
      label: 'No blocking problems',
      ok: check.errors.length === 0,
      detail: check.errors[0],
      extra: check.errors.slice(1, 6),
      fix: 'build',
      fixLabel: 'Open canvas',
    },
    {
      id: 'warnings',
      label: 'Every step is connected',
      ok: check.warnings.length === 0,
      optional: true,
      detail: check.warnings[0],
      extra: check.warnings.slice(1, 6),
      fix: 'build',
      fixLabel: 'Open canvas',
    },
  ];
  if (Array.isArray(aiProblems) && nodes.some((n) => n?.type === 'llm' || n?.type === 'ai-agent')) {
    const lines = aiProblems.map((p) => `${p.label}: ${p.message}`);
    items.push({
      id: 'ai-models',
      label: 'AI steps can reach their model',
      ok: aiProblems.length === 0,
      detail: lines[0],
      extra: lines.slice(1, 6),
      fix: 'build',
      fixLabel: 'Open canvas',
    });
  }
  if (imports.length) {
    items.push({
      id: 'imports', label: 'Import notes reviewed', ok: false, optional: true, detail: imports[0], extra: imports.slice(1, 6),
    });
  }
  const required = items.filter((i) => !i.optional);
  const done = required.filter((i) => i.ok).length;
  return {
    ready: done === required.length, done, total: required.length, items, check,
  };
}

/** Normalized node boxes + edges for a small preview diagram (0..1 coordinates). */
export function flowThumbnail(def, max = 14) {
  const nodes = (Array.isArray(def?.nodes) ? def.nodes : []).slice(0, max);
  if (!nodes.length) return { nodes: [], edges: [] };
  const pos = nodes.map((n, i) => {
    const p = n?.position;
    return (p && Number.isFinite(p.x) && Number.isFinite(p.y)) ? { x: p.x, y: p.y } : { x: 0, y: i * 120 };
  });
  const xs = pos.map((p) => p.x);
  const ys = pos.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const spanX = Math.max(...xs) - minX || 1;
  const spanY = Math.max(...ys) - minY || 1;
  const index = new Map(nodes.map((n, i) => [String(n.id), i]));
  const out = nodes.map((n, i) => ({
    id: String(n.id),
    type: n.type || 'step',
    x: Math.max(...xs) === minX ? 0.5 : (pos[i].x - minX) / spanX,
    y: Math.max(...ys) === minY ? 0.5 : (pos[i].y - minY) / spanY,
  }));
  const edges = (Array.isArray(def?.edges) ? def.edges : [])
    .map((e) => [index.get(String(e?.source)), index.get(String(e?.target))])
    .filter(([a, b]) => a != null && b != null && a !== b);
  return { nodes: out, edges };
}

export function stepCount(def) {
  return (Array.isArray(def?.nodes) ? def.nodes : []).filter((n) => n?.type !== 'start' && n?.type !== 'end').length;
}

/** Map of flow Mongo id → apps that use it (panels, chat flow). */
/** Flow Mongo id → apps that use it, each with `roles` (plain descriptions of where: the chat, or a panel). */
export function flowUsage(apps) {
  const usage = new Map();
  const add = (ref, row, role) => {
    const key = String(ref || '').trim();
    if (!key) return;
    if (!usage.has(key)) usage.set(key, []);
    const list = usage.get(key);
    const id = String(row?._id || '');
    let app = list.find((a) => a.id === id);
    if (!app) {
      app = { id, name: row?.name || row?.definition?.name || 'Untitled app', roles: [] };
      list.push(app);
    }
    if (!app.roles.includes(role)) app.roles.push(role);
  };
  (apps || []).forEach((row) => {
    const def = row?.definition || {};
    add(def.defaultFlowId, row, 'Chat');
    add(def.metadata?.interviewFlow?.defaultChatFlowRef, row, 'Chat');
    (Array.isArray(def.panels) ? def.panels : []).forEach((p) => {
      add(p?.source?.flowRef, row, p?.title ? `Panel “${p.title}”` : 'A panel');
    });
  });
  return usage;
}

/* ---------- Test input ---------- */

const isPrimitive = (v) => v == null || ['string', 'number', 'boolean'].includes(typeof v);

/**
 * Which part of the payload the simple form edits:
 * `{input: {...flat}}` → fields of `input`; `{input: "text"}` → a single message box;
 * a flat object → its own fields; anything else → JSON only.
 */
export function inputFormShape(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return { kind: 'json' };
  const keys = Object.keys(payload);
  if (keys.length === 1 && keys[0] === 'input') {
    const inner = payload.input;
    if (typeof inner === 'string') return { kind: 'message', path: ['input'] };
    if (inner && typeof inner === 'object' && !Array.isArray(inner) && Object.values(inner).every(isPrimitive)) {
      return { kind: 'fields', path: ['input'], fields: Object.keys(inner) };
    }
    return { kind: 'json' };
  }
  if (keys.length && Object.values(payload).every(isPrimitive)) return { kind: 'fields', path: [], fields: keys };
  return { kind: 'json' };
}

export function humanizeKey(key) {
  const s = String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : key;
}

export function readAt(obj, path) {
  return path.reduce((acc, k) => (acc && typeof acc === 'object' ? acc[k] : undefined), obj);
}

export function writeAt(obj, path, value) {
  if (!path.length) return value;
  const [head, ...rest] = path;
  return { ...(obj || {}), [head]: writeAt(obj?.[head], rest, value) };
}

/* ---------- Run timeline ---------- */

const tokenNode = (ev) => ev?.langgraph_v2?.metadata?.langgraph_node || ev?.metadata?.langgraph_node || ev?.node_id;

function tokenText(ev) {
  const d = ev?.data;
  const chunk = d?.chunk ?? d?.token ?? ev?.token ?? ev?.content;
  if (typeof chunk === 'string') return chunk;
  if (chunk && typeof chunk.content === 'string') return chunk.content;
  if (chunk && Array.isArray(chunk.content)) return chunk.content.map((c) => (typeof c === 'string' ? c : c?.text || '')).join('');
  return '';
}

const tsMs = (ev) => {
  const t = Date.parse(ev?.timestamp || '');
  return Number.isFinite(t) ? t : null;
};

function parseToolCalls(text) {
  if (typeof text !== 'string') return undefined;
  try {
    const calls = JSON.parse(text);
    return Array.isArray(calls) ? calls : undefined;
  } catch {
    return undefined;
  }
}

const NOISE_KEYS = new Set(['input', 'user_id', 'userInput', 'chatHistory', 'assetIds']);

/** The part of a finished run worth showing: `result` if the flow set one, else the variables it produced. */
export function readableResult(data) {
  if (!data || typeof data !== 'object') return data ?? null;
  if (data.result != null && !(typeof data.result === 'object' && !Object.keys(data.result).length)) return data.result;
  const vars = data.variables && typeof data.variables === 'object' ? data.variables : data;
  const out = {};
  Object.entries(vars).forEach(([k, v]) => {
    if (NOISE_KEYS.has(k) || k.startsWith('_') || k.startsWith('__')) return;
    out[k] = v;
  });
  return Object.keys(out).length ? out : null;
}

/**
 * Folds streamed execution events into one row per flow step (only ids that exist in the definition),
 * plus overall status, progress, final result and error.
 */
export function buildRunTimeline(events, def) {
  const nodes = Array.isArray(def?.nodes) ? def.nodes : [];
  const byId = new Map(nodes.map((n) => [String(n.id), n]));
  const rows = new Map();
  let status = events.length ? 'running' : 'idle';
  let progress = null;
  let result = null;
  let rawResult = null;
  let error = null;
  let totalTime = null;
  let executionId = null;
  let startedAt = null;
  let approval = null;

  const rowFor = (id) => {
    if (!rows.has(id)) {
      const n = byId.get(id);
      rows.set(id, {
        id,
        type: n?.type || 'step',
        label: (n?.label && String(n.label).trim()) || id,
        status: 'running',
        runs: 0,
        startedAt: null,
        duration: null,
        text: '',
        error: null,
        decision: null,
        model: null,
      });
    }
    return rows.get(id);
  };

  events.forEach((ev) => {
    const type = ev?.type;
    if (ev?.execution_id && !executionId) executionId = ev.execution_id;
    if (startedAt == null && tsMs(ev) != null) startedAt = tsMs(ev);
    if (ev?.progress?.total) progress = ev.progress;
    if (type === 'llm_token') {
      const id = String(tokenNode(ev) || '');
      if (byId.has(id)) rowFor(id).text += tokenText(ev);
      return;
    }
    if (ev?.event_type === 'on_chat_model_start') {
      const meta = ev.metadata || ev.langgraph_v2?.metadata || {};
      const id = String(meta.langgraph_node || '');
      if (byId.has(id) && (meta.ls_provider || meta.ls_model_name)) {
        rowFor(id).model = { provider: meta.ls_provider || '', model: meta.ls_model_name || '' };
      }
      return;
    }
    const id = String(ev?.node_id || '');
    if (type === 'approval_required') {
      status = 'waiting';
      approval = ev.approval || null;
      result = null;
      rawResult = null;
      const waitingId = String(approval?.nodeId || '');
      if (byId.has(waitingId)) rowFor(waitingId).status = 'waiting';
      return;
    }
    if (status === 'waiting' && (type === 'flow_started' || type === 'node_start')) {
      status = 'running';
      approval = null;
    }
    if (type === 'node_start' && byId.has(id)) {
      const row = rowFor(id);
      const resumed = row.status === 'waiting';
      row.status = 'running';
      if (!resumed) row.runs += 1;
      row.startedAt = tsMs(ev);
      row.error = null;
    } else if (type === 'node_complete' && byId.has(id)) {
      const row = rowFor(id);
      row.status = 'done';
      if (!row.runs) row.runs = 1;
      const explicit = Number(ev.node_duration);
      const end = tsMs(ev);
      if (Number.isFinite(explicit) && explicit > 0) row.duration = explicit;
      else if (row.startedAt != null && end != null) row.duration = Math.max(0, (end - row.startedAt) / 1000);
      const out = ev?.data?.output;
      if (out && typeof out === 'object') {
        const branch = Object.entries(out).find(([k]) => k.startsWith('conditional_response_'))?.[1];
        if (branch?.next_path) row.decision = String(branch.next_path);
        const human = row.type === 'human-input' ? out[`human_response_${id}`] : null;
        if (human && !human.paused && human.next_path) row.decision = String(human.next_path);
        if (typeof out.error === 'string' && out.error.trim()) {
          row.status = 'failed';
          row.error = out.error;
        }
        const switched = out[`llm_fallback_${id}`];
        if (switched && typeof switched === 'object' && switched.to) row.fallback = switched;
        const calls = parseToolCalls(out[`skill_tool_calls_${id}`]) ?? out.variables?.skill_tool_calls;
        if (typeof out.ai_response === 'string' && Array.isArray(calls)) {
          row.toolCalls = calls.filter((c) => c && typeof c === 'object').slice(0, 20);
        }
      }
    } else if (type === 'node_error' && byId.has(id)) {
      const row = rowFor(id);
      row.status = 'failed';
      row.error = ev.error || ev.message || ev?.data?.error || 'This step failed';
    } else if (type === 'flow_complete') {
      status = 'done';
      rawResult = ev.data ?? ev.result ?? null;
      result = readableResult(rawResult);
      totalTime = Number.isFinite(Number(ev.execution_time)) ? Number(ev.execution_time) : null;
    } else if (type === 'flow_error' || type === 'error' || type === 'execution_timeout') {
      status = 'failed';
      error = ev.error || ev.message || 'The run failed';
    }
  });

  if (status !== 'running') {
    rows.forEach((row) => {
      if (row.status === 'running') row.status = status === 'failed' ? 'stopped' : 'done';
    });
  }
  if (status !== 'waiting') {
    rows.forEach((row) => {
      if (row.status === 'waiting') row.status = status === 'failed' ? 'stopped' : 'done';
    });
  }
  if (totalTime == null && !['running', 'waiting'].includes(status) && startedAt != null) {
    const last = [...events].reverse().map(tsMs).find((t) => t != null);
    if (last != null) totalTime = Math.max(0, (last - startedAt) / 1000);
  }
  return {
    status, rows: [...rows.values()], progress, result, rawResult, error, totalTime, executionId, approval,
  };
}

export function formatSeconds(s) {
  if (s == null || !Number.isFinite(s)) return '';
  if (s < 1) return `${Math.max(1, Math.round(s * 1000))} ms`;
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)} s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
}
